import { Controller } from "@hotwired/stimulus"

// Inline rename for a form-builder section title.
//
// Click the title (or arrive with autoedit=true, as a freshly added section
// does) and the heading becomes a focused, selected text input. Enter or blur
// saves through form_sections#update (the turbo-stream response replaces the
// whole section card, which also disconnects this controller); Esc cancels.
//
// Markup contract: the controller element wraps the section card; a `title`
// target is the heading element; `url` is the section's update path.
export default class extends Controller {
  static targets = ["title"]
  static values = { url: String, autoedit: Boolean }

  connect() {
    if (this.autoeditValue) {
      // Wait a tick so the stream-inserted node is laid out before focusing.
      requestAnimationFrame(() => this.edit())
    }
  }

  edit(event) {
    if (event) event.preventDefault()
    if (this.input || !this.hasTitleTarget) return

    this.heading = this.titleTarget
    this.original = this.heading.textContent.trim()
    this.finished = false

    const input = document.createElement("input")
    input.type = "text"
    input.value = this.original
    input.maxLength = 255
    input.setAttribute("aria-label", "Section title")
    // Same 28px height as the heading, so swapping it in/out never shifts the
    // layout (a shift between mousedown and mouseup would eat a click on the
    // control that blurred it).
    input.className = "min-w-0 flex-1 rounded border border-blue-500 bg-white h-7 px-2 py-0 text-lg leading-7 font-semibold text-blue-800 focus:outline-none"
    input.dataset.sectionTitleInput = "true"
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); this.save() }
      else if (e.key === "Escape") { e.preventDefault(); this.cancel() }
    })
    input.addEventListener("blur", () => this.save())

    this.input = input
    this.heading.hidden = true
    this.heading.after(input)
    input.focus()
    input.select()
  }

  cancel() {
    if (this.finished) return
    this.finished = true
    this.teardown()
  }

  async save() {
    if (this.finished || !this.input) return
    this.finished = true

    const title = this.input.value.trim()
    if (!title || title === this.original) {
      this.teardown()
      return
    }

    // Show the new title immediately; the stream replace follows.
    this.heading.textContent = title
    this.teardown()

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({ form_section: { title } })
      })
      Turbo.renderStreamMessage(await response.text())
    } catch (error) {
      console.error("Error renaming section:", error)
      this.heading.textContent = this.original
    }
  }

  teardown() {
    if (this.input) this.input.remove()
    this.input = null
    if (this.heading) this.heading.hidden = false
  }
}
